import { Epic, ofType } from 'redux-observable';
import { EMPTY, from, of, timer } from 'rxjs';
import { catchError, concatMap, exhaustMap, expand, map, switchMap, takeUntil, toArray } from 'rxjs/operators';

import { Project } from '../../types';
import { get } from '../api';

import { FETCH_ALL_PROJECTS, FETCH_PROJECTS, POLL_PROJECTS_START, POLL_PROJECTS_STOP } from './actions';

const fetchProjectsFulfilled = payload => ({ type: 'FETCH_PROJECTS_FULFILLED', payload: payload });

export const pollTasksEpic: Epic = (action$) => action$.pipe(
  ofType(POLL_PROJECTS_START),
  switchMap((action) =>
    timer(0, 3000).pipe(
      takeUntil(action$.pipe(
        ofType(POLL_PROJECTS_STOP),
      )),
      exhaustMap(() =>
        from(get(`/api/projects`, false, { organizationId: action.payload })).pipe(
          map(fetchProjectsFulfilled),
        ),
      ),
    ),
  ),
);

const paginationRegex = /items (\d+)-(\d+)\/(\d+)/;

// projects return the header content-range: items 0-11626/11627
const fetchProjectPage = (organizationId: number, offset: number, count: number) => from(
  get<Project[]>(`/api/projects`, false, { organizationId, count, offset }),
);

const parseMatch = (matches: RegExpMatchArray | null) => {
  if (!matches) {
    return null;
  }
  return {
    offset: Number(matches[1]),
    count: Number(matches[2]),
    totalCount: Number(matches[3]),
  };
};

const fetchAllProjectsFulfilled = (data: Project[]) => ({
  type: `${FETCH_PROJECTS}_FULFILLED`,
  payload: {
    data,
  },
});

export const fetchAllProjects: Epic = (action$) => action$.pipe(
  ofType(FETCH_ALL_PROJECTS),
  switchMap(action => (
    fetchProjectPage(action.organizationId, 0, action.count).pipe(
      expand(response => {
        if (response?.data.length === 0 || !response.headers.has('content-range')) {
          return EMPTY;
        }
        const range = response.headers.get('content-range') as string;
        const matches = range.match(paginationRegex);
        const pageData = parseMatch(matches);
        if (!pageData) {
          throw new Error(`Pagination response from server was invalid`);
        }
        // the projects api filters after using offset + limit
        // the number of results returned can be lower than action.count
        // and there can be remaining results
        const offset = pageData.offset + action.count;
        if (offset >= pageData.totalCount) {
          return EMPTY;
        }
        return fetchProjectPage(action.organizationId, offset, action.count);
      }),
      concatMap(response => response?.data ?? []),
      toArray(),
    )
  )),
  map(fetchAllProjectsFulfilled),
  catchError(error => of({
    type: `${FETCH_PROJECTS}_REJECTED`,
    payload: error,
    error: true,
  })),
);
